import { ButtonTertiary } from "@nova/component-btn"
import { Status, StatusProps } from "@nova/ds-component-status"
import classNames from "classnames"
import { HTMLAttributes, useMemo } from "react"
import * as Icons from "@nova/component-icons"
import { NonSensitiveFlightRecordConnectedAccount, SyncStatus } from "@nova/schemas"
import {
    usePilotTrackingConnectionsContext,
    SyncInfo,
} from "features/data-management/pilot-tracking/PilotTrackingConnectionsContext"
import { describeRelativeTime } from "@nova/format"
import { useIsEditor } from "tools/hooks/useIsEditor"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { setActiveContextMenu } from "features/popups/slice"
import {
    ContextMenu,
    ContextMenuItem,
} from "features/data-management/workspace/TODO-put-in-package/ContextMenu"
import { selectActiveContextMenu } from "features/popups/contextMenu"

const TAGS = {
    notYetSynced: {
        type: "neutral",
        variant: "outline",
    },
    syncing: {
        type: "status",
        variant: "outline",
    },
    synced: {
        type: "positive",
        variant: "outline",
    },
    error: {
        type: "error",
        variant: "outline",
    },
} satisfies Record<string, StatusProps>

type SyncStatusDisplay = {
    statusMessage: string | undefined
    tagProps: StatusProps
    tagMessage: string
}

const SYNC_STATUS_DISPLAY: Record<SyncInfo["status"], (syncInfo: SyncInfo) => SyncStatusDisplay> = {
    syncing: (syncInfo) => {
        const { filesTotal, filesSynced, triggeredAt } = syncInfo as Extract<
            SyncInfo,
            { status: "syncing" }
        >
        const progressLabel =
            filesTotal != null
                ? `${filesSynced} / ${filesTotal} flights synced`
                : `Began syncing ${describeRelativeTime(triggeredAt)}`
        return {
            statusMessage: progressLabel,
            tagProps: TAGS["syncing"],
            tagMessage: "Syncing",
        }
    },
    synced: (syncInfo) => ({
        statusMessage: `Last synced ${describeRelativeTime(syncInfo.triggeredAt)}`,
        tagProps: TAGS["synced"],
        tagMessage: "Up to date",
    }),
    error: (syncInfo) => ({
        statusMessage: `Failed to sync ${describeRelativeTime(syncInfo.triggeredAt)}`,
        tagProps: TAGS["error"],
        tagMessage: "Failed",
    }),
}

export const ConnectedAccount = ({
    account,
    onSync,
    contextMenuSections,
    ...rest
}: {
    account: NonSensitiveFlightRecordConnectedAccount
    onSync: (id: string) => void
    contextMenuSections: ContextMenuItem[][]
} & HTMLAttributes<HTMLDivElement>) => {
    const dispatch = useAppDispatch()
    const isEditor = useIsEditor()
    const activeContextMenu = useAppSelector(
        selectActiveContextMenu("PiotTrackingConnectedAccount")
    )
    const contextMenuIsShown = activeContextMenu?.data.id === account.id

    const { syncStatuses, isLoading } = usePilotTrackingConnectionsContext()
    const { firstName, lastName } = account
    const fullName = firstName ? firstName + (lastName ? ` ${lastName}` : "") : undefined

    const syncStatus = syncStatuses?.[account.id]
    const syncStatusDisplay = useMemo((): SyncStatusDisplay | undefined => {
        if (isLoading) return undefined
        if (!syncStatus)
            return {
                statusMessage: undefined,
                tagProps: TAGS["notYetSynced"],
                tagMessage: "Not yet synced",
            }
        return SYNC_STATUS_DISPLAY[syncStatus.status](syncStatus)
    }, [syncStatus, isLoading])

    if (!syncStatusDisplay || isLoading) return null
    const { statusMessage, tagProps, tagMessage } = syncStatusDisplay

    return (
        <div
            onContextMenu={(e) => {
                e.preventDefault()
                if (!isEditor) return
                dispatch(
                    setActiveContextMenu({
                        type: "PiotTrackingConnectedAccount",
                        data: { id: account.id, fixedPosition: true },
                        position: { top: e.clientY, left: e.clientX },
                    })
                )
            }}
            {...rest}
            className={classNames("flex w-full items-center justify-between", rest.className)}
        >
            {/* Account Info & Last Sync Message */}
            <div className="flex flex-col gap-1 min-w-0">
                <h5 className="text-gray-0">
                    {account.accountId}
                    {fullName && <> &bull; {fullName}</>}
                </h5>
                <h5 className="text-gray-300">
                    {!!statusMessage && <span>{statusMessage} &bull; </span>}
                    Weekly auto-sync
                </h5>
            </div>

            {/* Right Side */}
            <div className="flex gap-5 items-center relative ml-5 flex-shrink-0">
                <Status {...tagProps} className="flex-shrink-0">
                    {tagMessage}
                </Status>
                <ButtonTertiary
                    title="Sync"
                    LeftIcon={Icons.Sync}
                    onClick={() => onSync(account.id)}
                    className="flex-shrink-0"
                    disabled={!isEditor || syncStatus?.status === SyncStatus.Syncing}
                />

                {isEditor && (
                    <div className="relative">
                        <Icons.SmallIcon
                            Icon={Icons.ThreeDotsSettings}
                            className={classNames("cursor-pointer flex-shrink-0", {
                                "text-gray-100 hover:text-gray-0 active:text-gray-200":
                                    !contextMenuIsShown,
                                "text-gray-0": contextMenuIsShown,
                            })}
                            onClick={() => {
                                dispatch(
                                    setActiveContextMenu({
                                        type: "PiotTrackingConnectedAccount",
                                        data: { id: account.id, fixedPosition: false },
                                        position: {
                                            top: 0,
                                            left: 0,
                                        },
                                    })
                                )
                            }}
                        />
                        {contextMenuIsShown && (
                            <ContextMenu
                                sections={contextMenuSections}
                                onBlur={() => dispatch(setActiveContextMenu(undefined))}
                                isFixedPosition={activeContextMenu.data.fixedPosition}
                                style={{ top: "1.75rem", right: "0rem" }}
                                top={activeContextMenu.position.top}
                                left={activeContextMenu.position.left}
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}
